import { useState } from "react";
import {
  Sparkles,
  ArrowRight,
  AlertTriangle,
  Flame,
  Clock,
  RefreshCw,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import { Button } from "@/design-system/components/ui/button";
import { Slider } from "@/design-system/components/ui/slider";
import { useAnalyzeSymptom } from "./hooks/useAnalyzeSymptom";
import { formatMoney } from "@fysaro/shared";
import { useTranslation } from "react-i18next";
import { redirectToApp } from "@/utils/appUrl";

interface PublicClinicAIWidgetProps {
  clinicId: string;
}

export function PublicClinicAIWidget({ clinicId }: PublicClinicAIWidgetProps) {
  const { t, i18n } = useTranslation(["common", "landing"]);
  const [prompt, setPrompt] = useState("");
  const [userPain, setUserPain] = useState<number>(5);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const {
    analyze,
    isLoading,
    data: aiResponse,
    isSuccess,
    reset: resetMutation,
  } = useAnalyzeSymptom();

  const analysis = aiResponse?.analysis;

  const currentPain =
    analysis?.suggestedPainLevel !== undefined
      ? analysis.suggestedPainLevel
      : userPain;

  const handleAnalyzeSymptom = async () => {
    if (!prompt.trim()) return;
    await analyze({ patientText: prompt, clinicId });
  };

  const handleConfirmAndBook = () => {
    if (!aiResponse || !aiResponse.analysis || !aiResponse.sessionId) return;
    setIsRedirecting(true);
    const { sessionId } = aiResponse;
    const targetPath = `/patient/bookings/landing?sessionId=${sessionId}`;
    redirectToApp(targetPath);
  };

  return (
    <div className="relative overflow-hidden bg-card border border-border/60 rounded-2xl p-5 md:p-6 shadow-md transition-all duration-300">
      {/* HEADER DEL WIDGET (Semántica pura) */}
      <div className="flex items-start gap-3.5 mb-5">
        <div className="p-2.5 bg-primary rounded-xl text-primary-foreground shadow-sm shrink-0">
          <Sparkles
            size={18}
            className={isLoading ? "animate-spin" : ""}
            fill="currentColor"
          />
        </div>
        <div className="text-left">
          <span className="text-[9px] font-black uppercase tracking-widest text-primary bg-primary/10 px-2 py-0.5 rounded-md">
            {t("landing:ai.title")}
          </span>
          <h3 className="font-bold text-sm md:text-base text-foreground uppercase tracking-wide mt-1">
            {t("landing:ai.ask_title")}
          </h3>
          <p className="text-xs text-muted-foreground font-medium mt-0.5 leading-snug">
            {t("landing:ai.sub_title")}
          </p>
        </div>
      </div>

      {!isSuccess ? (
        <div className="space-y-4">
          {/* CAJA DE TEXTO LIMPIA (Sin botón de voz redundante) */}
          <div className="relative rounded-xl border border-border bg-muted/30 shadow-inner transition-all focus-within:ring-2 focus-within:ring-primary/10 focus-within:border-primary">
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              disabled={isLoading}
              maxLength={800}
              placeholder={t("landing:ai.placeholder")}
              className="w-full min-h-[100px] p-3.5 bg-transparent text-xs font-medium outline-none resize-none disabled:opacity-60 text-foreground placeholder:text-muted-foreground/60"
            />
          </div>

          <Button
            onClick={handleAnalyzeSymptom}
            disabled={isLoading || !prompt.trim()}
            className="w-full rounded-xl font-bold text-xs uppercase tracking-wider h-10 px-5 shadow-sm transition-transform active:scale-95"
          >
            {isLoading
              ? t("landing:ai.analyze_loading")
              : t("landing:ai.analyze_cta")}
            <ArrowRight size={13} className="ml-2" />
          </Button>
        </div>
      ) : (
        /* 📊 RESULTADOS DEL TRIAJE */
        <div className="space-y-5 animate-in fade-in-40 slide-in-from-bottom-3 duration-300">
          {/* Interpretación sintética */}
          <div className="p-3.5 bg-muted/40 rounded-xl border border-border/40 space-y-1 text-left">
            <div className="flex items-center gap-1 text-[9px] font-black uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
              <CheckCircle2 size={12} /> {t("landing:ai.result_title")}
            </div>
            <p className="text-xs text-foreground/90 font-semibold leading-relaxed">
              {analysis?.summary}
            </p>
            {aiResponse?.requiresUrgentCare && (
              <p className="text-xs text-destructive font-semibold leading-relaxed pt-1">
                {analysis?.explanation}
              </p>
            )}
          </div>

          {/* Recomendación de tratamiento */}
          <div
            className={
              aiResponse?.requiresUrgentCare ? "hidden" : "space-y-1.5 text-left"
            }
          >
            <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground">
              {t("landing:ai.suggestion")}
            </span>
            <div className="flex flex-col gap-3 p-3.5 rounded-xl bg-secondary/30 border border-border/60">
              <div className="space-y-1">
                <span className="font-bold text-xs text-foreground uppercase tracking-wide block">
                  {analysis?.serviceName}
                </span>
                <p className="text-[11px] text-muted-foreground font-medium leading-normal">
                  {analysis?.explanation}
                </p>
              </div>

              {/* Bloque de duración y precio */}
              <div className="flex items-center gap-3 bg-card px-3 py-1.5 rounded-lg border border-border/40 w-fit">
                <div className="flex items-center gap-1 text-[11px] font-bold text-foreground">
                  <Clock size={12} className="text-primary" />
                  {analysis?.serviceDuration} {t("common:durations.short_min")}
                </div>
                <div className="h-2.5 w-[1px] bg-border" />
                <div className="flex items-center gap-0.5 text-[11px] font-bold text-emerald-600 dark:text-emerald-400">
                  {formatMoney(
                    analysis?.servicePrice ?? 0,
                    "EUR",
                    i18n.language,
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ðŸŽšï¸ FIX SLIDER: CON TRACK VISIBLE Y LIMPIO */}
          <div
            className={
              aiResponse?.requiresUrgentCare
                ? "hidden"
                : "space-y-2 bg-muted/20 p-3.5 rounded-xl border border-border/40 text-left"
            }
          >
            <div className="flex justify-between items-center text-[11px] font-bold text-muted-foreground">
              <span className="flex items-center gap-1">
                <Flame size={13} className="text-orange-500" />{" "}
                {t("landing:ai.pain_title")}:
              </span>
              <span className="text-foreground font-black bg-card border px-2 py-0.5 rounded shadow-sm text-xs">
                {currentPain} / 10
              </span>
            </div>
            <div className="pt-1.5 relative">
              <Slider
                value={[currentPain]}
                max={10}
                min={1}
                step={1}
                // Forzamos clases directas al root del Slider para pintar el track inactivo sin romper variables globales
                className="py-2 cursor-pointer [&>span:first-child]:h-2 [&>span:first-child]:bg-foreground/10 [&>span:first-child>span]:bg-primary"
                onValueChange={(val) => setUserPain(val[0])}
              />
              <div className="flex justify-between text-[8px] text-muted-foreground/80 font-bold uppercase tracking-wider mt-1">
                <span>{t("landing:ai.pain_level_1")}</span>
                <span>{t("landing:ai.pain_level_2")}</span>
                <span>{t("landing:ai.pain_level_3")}</span>
              </div>
            </div>
          </div>

          {/* Alertas Médicas (Red Flags) */}
          {analysis?.redFlags && analysis.redFlags.length > 0 && (
            <div className="p-3 bg-destructive/5 border-l-2 border-destructive rounded-r-lg flex items-start gap-2.5 text-left">
              <AlertTriangle
                size={14}
                className="text-destructive shrink-0 mt-0.5"
              />
              <div className="text-[11px] text-foreground/90 font-medium leading-relaxed">
                <span className="font-bold block uppercase tracking-wider text-destructive text-[9px] mb-0.5">
                  {t("landing:ai.red_flag_title")}
                </span>
                {t("landing:ai.red_flag_sub_title")}:{" "}
                <b className="text-foreground font-bold">
                  {analysis.redFlags.join(", ")}
                </b>
                .
              </div>
            </div>
          )}

          {/* ACCIONES DE CONVERSIÓN */}
          <div className="flex flex-col gap-3 pt-3 border-t border-border/60">
            {!aiResponse?.requiresUrgentCare && (
              <Button
                className="w-full rounded-xl font-bold text-xs uppercase tracking-wider h-11 shadow-sm bg-primary text-primary-foreground hover:bg-primary/90 gap-1.5"
                onClick={handleConfirmAndBook}
                disabled={isRedirecting}
              >
                <span>{t("landing:ai.select_specialist")}</span>
                {isRedirecting ? <Loader2 size={13} className="animate-spin" /> : <ArrowRight size={13} />}
              </Button>
            )}

            <button
              onClick={() => {
                resetMutation();
                setPrompt("");
              }}
              className="flex items-center gap-1 justify-center text-[11px] font-bold text-muted-foreground hover:text-foreground transition-colors mx-auto py-1"
            >
              <RefreshCw size={11} />
              {t("landing:ai.reanalyze")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
